/* global window */
import getGlobalParam from '../getGlobalParam/index.ts';
import useOverriddenState, { OverriddenStateOptions } from '../useOverriddenState/index.ts';

export type SerializableScalar = string | number | boolean | null | undefined;

export type SerializableObject = {
  [key: string]: SerializableScalar | SerializableObject | SerializableArray;
};

export type SerializableArray = Array<SerializableScalar | SerializableObject | SerializableArray>;

export type Serializable = SerializableScalar | SerializableObject | SerializableArray;

export interface UseStorageOptions<T> extends OverriddenStateOptions<T> {
  /**
   * Allows namespace to prefix the storage key
   * @default 'aglmd-web-components-'
   */
  prefix?: string;
  /**
   * If `true` use `sessionStorage` instead of `localStorage` + pull from `sessionId` global param if available
   * @default false
   */
  session?: boolean;
  /**
   * Allows overriding the initial value with this value if not null, see useOverriddenState for details
   */
  overrideWith?: T | null;
  /**
   * unpacks the stored storage value into a full instance of what is being stored
   * @default JSON.parse
   */
  unpack?: (stringified: string) => T;
  /**
   * Inverse of unpack: packs the storage value into a string to be unpack
   * @default JSON.stringify
   */
  pack?: (value: T) => string;
}

/**
 * A hook for storing a value within the browser, backed by localStorage and sessionStorage
 *
 * @param key The key to use for setting and getting the value
 * @param initialValue The value to use if no value is stored
 * @param options
 *
 * @example <caption>Example Usage</caption>
 * const [wasLastOpen, setOpen] = useStorage('drawer', false);
 */
export default function useStorage<T extends Serializable>(
  keyRaw: string,
  initialValue: T,
  {
    prefix = 'aglmd-web-components-',
    session = false,
    overrideWith = null,
    onChange,
    reducer,
    init = i => i,
    comparator,
    shouldUseOverride,
    pack = JSON.stringify,
    unpack = JSON.parse
  }: UseStorageOptions<T> = {}
): [T, (state: T | ((prevState: T) => T)) => void] {
  const store = window[session ? 'sessionStorage' : 'localStorage'];
  const key = `${prefix}${keyRaw}${
    session ? `-agilemd-session-${getGlobalParam('sessionId', '')}` : ''
  }`;
  return useOverriddenState(initialValue, overrideWith as T, {
    onChange(currentState) {
      try {
        store.setItem(key, pack(currentState));
      } catch (err) {
        console.warn(err);
      }
      if (onChange) {
        onChange(currentState);
      }
    },
    reducer,
    init(i) {
      try {
        const item = store.getItem(key);

        return item ? init(unpack(item) as T) : init(i);
      } catch {
        return init(i);
      }
    },
    comparator,
    shouldUseOverride
  });
}
